import { ConceptStatusValidityInfo, LabActionType, LabInputType, LabItemAction, LabItemBMDAction } from '@fusion-advantage/formlib'
import L1Select from '../../form/l1/L1Select'
import { V2L2BarcodeScanField } from '../Field/V2L2BarcodeScanField'
import { RenderPlatform } from '@fusion-advantage/backcompat'
import { L2ErfRow, L2ErfRowContent, L2ErfRowControls } from '../../form/l2/L2ErfRow'
import L2Erf from '../../form/l2/L2Erf'
import { ComplexElementSize } from '../../form/ComplexElementSize'
import L1PurifiedHtml from '../../form/l1/L1PurifiedHtml'
import { V2L2CaptionedFieldContainer } from '../Field/V2L2CaptionedFieldContainer'
import L1Boolean from '../../form/l1/L1Boolean'
import { LabRow } from '../../../redux/labsSlice'
import { V2L1FieldValidityCaption } from '../Indicator/V2L1FieldValidityCaption'
import V2L3LabsTableBmdControl from './V2L3LabsTableBmdControl'

interface V2L3LabsTableProps {
	rows: LabRow[]
	platform: RenderPlatform
	onActionChange: (stateIdx: number, action: LabItemAction) => void
	onBmdActionChange: (stateIdx: number, action: Partial<LabItemBMDAction>) => void
	onScanClick: (stateIdx: number) => void
}

export const V2L3LabsTable = ({rows, platform, onActionChange, onBmdActionChange, onScanClick}: V2L3LabsTableProps) => {
	return (
		<L2Erf
			controlTitles={['Lab Name']}
			contentTemplateColumns="auto auto auto minmax(min-content, 25%)"
			contentTitles={['Kit Code / Action', 'Unable to leave/collect', 'Result', 'Detail']}
		>
			{rows.map((row, stateIdx) => {
				let { model: item } = row
				const declineReasons = [...row.declineReasons]
				if(item.action?.type === LabActionType.Decline && !declineReasons.includes(item.action.reason)) {
					declineReasons.unshift(item.action.reason)
				}
				let disabledField: 'input'|'decline'|null = null
				switch(item.action?.type) {
					case LabActionType.BMD:
					case LabActionType.Check:
					case LabActionType.Codes:
						disabledField = 'decline'
						break
					case LabActionType.Decline:
						disabledField = 'input'
						break
				}
				const validity = row.inputValidity
				const severity = validity ? new ConceptStatusValidityInfo(validity).severity : undefined
				const codeCaption = ((severity !== undefined) && validity?.message) ? (
					<V2L1FieldValidityCaption
						severity={severity}
						title={validity.message}
					/>
				) : undefined

				return (
					<L2ErfRow key={stateIdx}>
						<L2ErfRowControls alignItems="flex-start">
							{item.labname}
						</L2ErfRowControls>
						<L2ErfRowContent>
							{disabledField === 'input' ? <div>—</div> : (
								<V2L2CaptionedFieldContainer caption={codeCaption}>
									{row.inputType === LabInputType.Codes ? ( 
										<V2L2BarcodeScanField
											platform={platform}
											size={ComplexElementSize.Medium}
											value={item.action?.type === LabActionType.Codes ? item.action.code : undefined}
											onChange={(e) => onActionChange(stateIdx, { type: LabActionType.Codes, code: e.target.value?.toUpperCase() })}
											onScanClick={() => onScanClick(stateIdx)}
											severity={severity}
										/>
									) : null}
									{row.inputType === LabInputType.Check ? (
										<L1Boolean
											inputType="checkbox"
											label="Performed"
											checked={item.action?.type === LabActionType.Check && item.action.performed}
											onChange={(e) => onActionChange(stateIdx, { type: LabActionType.Check, performed: e.target.checked })}
										/>
									) : null}
									{row.inputType === LabInputType.BMD ? (
										<V2L3LabsTableBmdControl 
											action={item.action?.type === LabActionType.BMD ? item.action : null}
											onChange={(action) => onBmdActionChange(stateIdx, action)}
										/>
									) : null}
									{row.inputType === LabInputType.Unspecified ? <div>—</div> : null}
								</V2L2CaptionedFieldContainer>
							)}
						</L2ErfRowContent>
						<L2ErfRowContent>
							{disabledField === 'decline' ? <div>—</div> : (
								<L1Select
									value={item.action?.type === LabActionType.Decline ? item.action.reason : undefined}
									onChange={(e) => onActionChange(stateIdx, { type: LabActionType.Decline, reason: e.target.value })}
								>
									{declineReasons.map((reason, idx) => (
										<option key={idx} value={reason}>{reason}</option>
									))}
								</L1Select>
							)}
						</L2ErfRowContent>
						<L2ErfRowContent>
							<L1PurifiedHtml content={item.result ?? undefined} />
						</L2ErfRowContent>
						<L2ErfRowContent>
							<L1PurifiedHtml content={item.resultdetail ?? undefined} />
						</L2ErfRowContent>
					</L2ErfRow>
				)
			})}
		</L2Erf>
	)
}
